import { FilePlus2 } from "lucide-react";
import * as React from "react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import {
  Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogForm, DialogHeader, DialogTitle,
  DiscardGuard,
} from "@/components/ui/dialog";
import { Callout } from "@/components/ui/feedback";
import { Field } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { UsPhoneInput } from "@/components/ui/phone-input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/components/ui/toast";
import { SendContractDialog } from "@/features/send-contract";
import { ACCIDENT_ROLES, roleText } from "@/lib/accident";
import {
  ACCIDENT_FACT_KEYS, DOB_KEY, GUARDIAN_NAME_KEY, GUARDIAN_RELATION_KEY, agreementFor, agreementText, copiedFacts,
  emailProblem, fieldsByKey, listsOf, missingFacts, onList, personValues, samePhone, sharedFacts, startsTicked, todayIn,
  writable,
} from "@/lib/accident-person";
import type { DialWith } from "@/lib/accident-person";
import { ApiError } from "@/lib/api";
import { contactName } from "@/lib/contacts";
import {
  useContactFields, useContractCheck, useCreateContact, useLeadAccident, useLinkAccident, useUpdateContactFields,
} from "@/lib/queries";
import { US_PHONE_PROBLEM, usPhone } from "@/lib/phone-number";
import type { AccidentRole, LeadCard } from "@/lib/types";
import { cn, formatPhoneNumber } from "@/lib/utils";

/**
 * New case (ACL-326 step 3, ADR-220 in the platform repo), in place of Add a person (ACL-510). A record
 * is one person's case from one accident, so a new one asks two things: who it is for, this person
 * (a client in a second crash) or someone else (a passenger, a child, a wife in her own crash); and
 * which accident, the same one as this record's, which copies the crash's facts (`ACCIDENT_FACT_KEYS`)
 * and asks their role, or a new one, which starts with no facts. This person in the same accident is
 * not offered: that is the same case twice. The dialog opens from the accident card's Add a party/case
 * (on the record and on the call's card) and from the person's page, and is titled by the second
 * answer: "Add to accident A-000123" until "A new accident" is picked, "New case" then.
 *
 * The record starts with the caller's firm, state, timezone and language. The agreement follows the
 * date of birth, as the contract service decides it: under 18 the firm's minor agreement, which needs
 * the parent or legal guardian. Then Send contract opens for the new record, where the deployment has
 * a contract service.
 *
 * The writes: the contact (`POST /leads`, with `person_of` for this person, `new_accident` for a new
 * accident); for the same accident its link (`POST /leads/{id}/accident/links`), as Add a person
 * linked; then its fields. A write that fails after the contact is made is said with the step that
 * failed, and pressing again carries on from there, so a second record is never made for one person.
 * A dialog, so the call and the page under it stay where they are.
 *
 * Whether the new person is called is the rep's to say (ADR-225): "Add them to the dial list", the
 * campaign the rep is dialing for (`dialWith`), ticked for a number of their own and unticked on the
 * caller's own number, which that record already has dialed. Ticked, the record joins the list as it
 * is made (`campaign_id`), waiting for the call to end when made on one (`after_call`).
 */
export function NewCaseButton({
  source, dialWith = null, className, variant = "outline",
}: {
  source: LeadCard;
  /** The list the new person may be called from; null where there is none to name. */
  dialWith?: DialWith | null;
  className?: string;
  variant?: "outline" | "ghost";
}) {
  const [open, setOpen] = React.useState(false);
  const [added, setAdded] = React.useState<LeadCard | null>(null);
  const [sending, setSending] = React.useState(false);
  const check = useContractCheck(added?.id);
  const offered = React.useRef<string | null>(null);
  // Send contract is offered once per record made, and only where there is a contract service.
  React.useEffect(() => {
    if (!added || !check.data || offered.current === added.id) return;
    offered.current = added.id;
    if (check.data.reason !== "not_set_up") setSending(true);
  }, [added, check.data]);

  return (
    <>
      <Button size="sm" variant={variant} className={className} onClick={() => setOpen(true)}>
        <FilePlus2 />
        New case
      </Button>
      <NewCaseDialog open={open} onOpenChange={setOpen} source={source} dialWith={dialWith} onAdded={setAdded} />
      {added && (
        <SendContractDialog
          open={sending}
          onOpenChange={setSending}
          contact={added}
          check={check.data}
          checking={check.isFetching}
        />
      )}
    </>
  );
}

type Step = "create" | "link" | "fields";
const STEP_WORDS: Record<Step, string> = {
  create: "Could not make the case",
  link: "was made, but not linked into this accident",
  fields: "was made, but its details did not save",
};
const NO_ROLE = "none";
type Who = "this" | "other";
type Where = "same" | "new";

/** One of the two questions: a row of choices, native radios the page's styles dress. */
function Choice<T extends string>({
  name, legend, value, options, onChange,
}: {
  name: string;
  legend: string;
  value: T;
  options: { value: T; label: string; hint?: string; disabled?: boolean }[];
  onChange: (value: T) => void;
}) {
  return (
    <fieldset className="space-y-1.5">
      <legend className="text-sm font-medium text-text">{legend}</legend>
      <div className="grid grid-cols-2 gap-2">
        {options.map((option) => (
          <label
            key={option.value}
            className={cn(
              "flex cursor-pointer items-start gap-2 rounded-md border px-3 py-2 text-sm",
              value === option.value ? "border-accent-8 bg-accent-2" : "border-border",
              option.disabled && "cursor-not-allowed opacity-60",
            )}
          >
            <input
              type="radio"
              name={name}
              value={option.value}
              checked={value === option.value}
              disabled={option.disabled}
              onChange={() => onChange(option.value)}
              className="mt-0.5"
            />
            <span className="min-w-0">
              <span className="block font-medium">{option.label}</span>
              {option.hint && <span className="block text-xs text-text-muted">{option.hint}</span>}
            </span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

export function NewCaseDialog({
  open, onOpenChange, source, dialWith = null, onAdded,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The record the case is made from: the caller, usually. */
  source: LeadCard;
  /** The list the new person may be called from (ADR-225); null where there is none to name. */
  dialWith?: DialWith | null;
  onAdded?: (card: LeadCard) => void;
}) {
  const toast = useToast();
  const fields = useContactFields(open ? source.id : undefined);
  const accident = useLeadAccident(open ? source.id : null);
  const create = useCreateContact();
  const link = useLinkAccident();
  const save = useUpdateContactFields();
  const sourceName = contactName(source) ?? formatPhoneNumber(source.phone);
  const sourceNumber = accident.data?.accident?.number ?? null;

  const [who, setWho] = React.useState<Who>("other");
  const [where, setWhere] = React.useState<Where>("same");
  const [firstName, setFirstName] = React.useState("");
  const [lastName, setLastName] = React.useState("");
  const [dateOfBirth, setDateOfBirth] = React.useState("");
  const [phone, setPhone] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [role, setRole] = React.useState<AccidentRole | null>("passenger");
  const [guardianName, setGuardianName] = React.useState("");
  const [guardianRelation, setGuardianRelation] = React.useState("");
  const [made, setMade] = React.useState<LeadCard | null>(null);
  const [linked, setLinked] = React.useState(false);
  const [failed, setFailed] = React.useState<{ step: Step; message: string } | null>(null);
  const [existing, setExisting] = React.useState<string | null>(null);
  const [problems, setProblems] = React.useState<Record<string, string>>({});
  const [busy, setBusy] = React.useState(false);
  // The rep's own answer to "Add them to the dial list"; null until they touch it, when the box
  // follows the number typed (ADR-225).
  const [callThem, setCallThem] = React.useState<boolean | null>(null);

  React.useEffect(() => {
    if (!open) return;
    setWho("other");
    setWhere("same");
    setFirstName("");
    setLastName("");
    setDateOfBirth("");
    setPhone(formatPhoneNumber(source.phone));
    setEmail("");
    setRole("passenger");
    setGuardianName(contactName(source) ?? "");
    setGuardianRelation("");
    setMade(null);
    setLinked(false);
    setFailed(null);
    setExisting(null);
    setProblems({});
    setCallThem(null);
  }, [open, source]);

  // This person's own date of birth is on their record: the case starts with it.
  const sourceBirth = fieldsByKey(fields.data).get(DOB_KEY)?.value;
  const thisPerson = who === "this";
  const sameAccident = where === "same";
  const pick = (next: Who) => {
    setWho(next);
    if (next === "this") {
      setWhere("new");
      setPhone(formatPhoneNumber(source.phone));
      setEmail(source.email ?? "");
      setDateOfBirth(typeof sourceBirth === "string" ? sourceBirth : "");
      setGuardianName("");
    } else {
      setEmail("");
      setDateOfBirth("");
      setGuardianName(contactName(source) ?? "");
    }
  };

  const facts = copiedFacts(fields.data);
  // What the accident holds (ADR-221) the new record reads at once; only a fact a record still keeps
  // on its own row (one in no accident yet) is copied.
  const shared = new Set(sharedFacts(fields.data).map((fact) => fact.key));
  const missing = fields.data ? missingFacts(fields.data) : [];
  const today = todayIn(source.clock_timezone ?? source.timezone);
  const agreement = agreementFor(dateOfBirth, today);
  const minor = agreement?.kind === "minor";
  const asksGuardian = minor && writable(fields.data, GUARDIAN_NAME_KEY);
  const asksRelation = minor && writable(fields.data, GUARDIAN_RELATION_KEY);
  const name = thisPerson
    ? sourceName
    : [firstName.trim(), lastName.trim()].filter(Boolean).join(" ") || "The new person";
  // Against what the dialog opens with: someone else in the same accident, the caller's number and
  // name filled in, passenger picked.
  const changed = who !== "other" || where !== "same" || firstName !== "" || lastName !== "" || dateOfBirth !== ""
    || guardianRelation !== "" || phone !== formatPhoneNumber(source.phone) || email !== "" || role !== "passenger"
    || guardianName !== (contactName(source) ?? "") || callThem !== null;
  const ownNumber = thisPerson || samePhone(phone, source.phone);
  const ticked = dialWith !== null && (callThem ?? startsTicked(thisPerson ? source.phone : phone, source.phone));

  const check = (): boolean => {
    const out: Record<string, string> = {};
    if (!thisPerson && !firstName.trim()) out.first_name = "A first name is required";
    if (!thisPerson && !phone.trim()) out.phone = "A number is required";
    else if (!thisPerson && !usPhone(phone)) out.phone = US_PHONE_PROBLEM;
    const badEmail = thisPerson ? null : emailProblem(email);
    if (badEmail) out.email = badEmail;
    if (!dateOfBirth) out.date_of_birth = "The date of birth decides the agreement";
    else if (!agreement) out.date_of_birth = "Pick a date of birth that has passed";
    if (asksGuardian && !guardianName.trim()) out[GUARDIAN_NAME_KEY] = "A minor's agreement needs the guardian";
    if (asksRelation && !guardianRelation.trim()) out[GUARDIAN_RELATION_KEY] = "Say how they are related";
    setProblems(out);
    return Object.keys(out).length === 0;
  };

  const run = async (duplicate = false) => {
    if (busy || !check()) return;
    const planned = personValues(fields.data, { dateOfBirth, guardianName, guardianRelation }, agreement);
    if (Object.keys(planned.problems).length > 0) {
      setProblems(planned.problems);
      return;
    }
    // A new accident starts with no facts of the crash, and a fact the accident holds is read by the
    // new record without a copy (ADR-221): only the person's own answers, and a fact the source still
    // keeps on its own row, are written.
    const values = Object.fromEntries(
      Object.entries(planned.values).filter(([key]) =>
        sameAccident ? !shared.has(key) : !ACCIDENT_FACT_KEYS.includes(key)),
    );
    setBusy(true);
    setFailed(null);
    let card = made;
    try {
      if (!card) {
        setExisting(null);
        try {
          card = await create.mutateAsync({
            // This person's own number, else the ten digits typed after the fixed +1, else the
            // caller's number as the record holds it.
            phone: thisPerson ? source.phone : (usPhone(phone) ?? phone.trim()),
            first_name: thisPerson ? source.first_name : firstName.trim(),
            last_name: thisPerson ? source.last_name : lastName.trim() || null,
            // Where the signing link is e-mailed too: this person's own, else as typed, else none yet.
            email: thisPerson ? (source.email ?? null) : (email.trim() || null),
            language: source.language,
            state: source.state,
            timezone: source.timezone,
            firm_id: source.firm?.id ?? null,
            // The caller's own number is meant: a child is reached through their parent's phone,
            // and this person's second case is on their own.
            duplicate: thisPerson || duplicate || samePhone(phone, source.phone),
            ...(thisPerson ? { person_of: source.id } : {}),
            ...(sameAccident ? {} : { new_accident: true, accident_role: role }),
            ...(ticked && dialWith ? { campaign_id: dialWith.campaignId, after_call: dialWith.callId } : {}),
          });
        } catch (e) {
          const body = e instanceof ApiError ? (e.body as { code?: string; existing_lead_id?: string } | undefined) : undefined;
          if (body?.code === "phone_exists" && body.existing_lead_id) {
            setExisting(body.existing_lead_id);
            return;
          }
          setFailed({ step: "create", message: e instanceof ApiError ? e.message : "Please try again." });
          return;
        }
        setMade(card);
      }
      if (sameAccident && !linked) {
        try {
          await link.mutateAsync({ leadId: source.id, otherId: card.id, relation: "same_accident", role });
        } catch (e) {
          setFailed({ step: "link", message: e instanceof ApiError ? e.message : "Please try again." });
          return;
        }
        setLinked(true);
      }
      if (Object.keys(values).length > 0) {
        try {
          await save.mutateAsync({ id: card.id, values });
        } catch (e) {
          setFailed({ step: "fields", message: e instanceof ApiError ? e.message : "Please try again." });
          return;
        }
      }
      // Asked for a list and refused one (do-not-call, a signed record of the same person on the
      // phone): the record is made all the same, and says so on its timeline.
      const listed = dialWith && ticked
        ? onList(listsOf(card), dialWith.campaignId) ? "On the dial list" : "Not put on the dial list: see the record"
        : null;
      toast.success(
        sameAccident ? `${contactName(card) ?? name} is in this accident` : `${contactName(card) ?? name} has a new case`,
        [roleText(role), agreement?.kind === "minor" ? "Minor agreement" : "Adult agreement", listed].filter(Boolean).join(" · "),
      );
      onOpenChange(false);
      onAdded?.(card);
    } finally {
      setBusy(false);
    }
  };

  const locked = made !== null;
  const sameLabel = sourceNumber ? `The same accident, ${sourceNumber}` : "The same accident";
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent variant="drawer" size="md">
        <DiscardGuard changed={changed} />
        <DialogHeader>
          {/* Titled by the accident picked: a party added to this one, or a case from a new one. */}
          <DialogTitle>
            {sameAccident ? (sourceNumber ? `Add to accident ${sourceNumber}` : "Add to this accident") : "New case"}
          </DialogTitle>
          <DialogDescription>
            {sameAccident ? (
              <>
                A passenger, a child, the driver, a relative. Their record starts in this accident, with the crash's
                details shared, and with {sourceName}'s firm, state, timezone and language.
              </>
            ) : (
              <>
                A record is one person's case from one accident. A new accident starts with no details of the crash,
                and with {sourceName}'s firm, state, timezone and language.
              </>
            )}
          </DialogDescription>
        </DialogHeader>
        {/* The form says what is wrong with a value in its own words, under the field (the e-mail too), not in
            the browser's bubble. */}
        <DialogForm noValidate onSubmit={(e) => { e.preventDefault(); void run(); }}>
          <DialogBody className="space-y-4">
            <Choice<Who>
              name="case-who"
              legend="Who is it for?"
              value={who}
              onChange={pick}
              options={[
                { value: "other", label: "Someone else", hint: "A passenger, a child, the driver, a relative." },
                { value: "this", label: sourceName, hint: "This person, in another accident." },
              ]}
            />
            <Choice<Where>
              name="case-where"
              legend="Which accident?"
              value={where}
              onChange={setWhere}
              options={[
                {
                  value: "same",
                  label: sameLabel,
                  hint: thisPerson ? "The same person in the same accident is this case." : "The crash's details are copied.",
                  disabled: thisPerson || locked,
                },
                { value: "new", label: "A new accident", hint: "Starts with no details of the crash.", disabled: locked },
              ]}
            />
            {!thisPerson && (
              <div className="grid grid-cols-2 gap-3">
                <Field label="First name" htmlFor="person-first" required error={problems.first_name}>
                  <Input id="person-first" autoFocus autoComplete="off" value={firstName} disabled={locked}
                    onChange={(e) => setFirstName(e.target.value)} />
                </Field>
                <Field label="Last name" htmlFor="person-last">
                  <Input id="person-last" autoComplete="off" value={lastName} disabled={locked}
                    onChange={(e) => setLastName(e.target.value)} />
                </Field>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <Field label="Date of birth" htmlFor="person-dob" required error={problems.date_of_birth}>
                {/* A button, not a text box: a password manager (LastPass) takes a date of birth for its form
                    fill, puts its icon over the browser's calendar and opens its own list instead. */}
                <DatePicker id="person-dob" max={today} today={today} value={dateOfBirth}
                  invalid={Boolean(problems.date_of_birth)} clearable={false} onChange={setDateOfBirth} />
              </Field>
              <Field label="Their role in the accident" htmlFor="person-role">
                <Select value={role ?? NO_ROLE} disabled={linked || (locked && !sameAccident)}
                  onValueChange={(value) => setRole(value === NO_ROLE ? null : (value as AccidentRole))}>
                  <SelectTrigger id="person-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NO_ROLE}>Not said</SelectItem>
                    {ACCIDENT_ROLES.map((r) => (
                      <SelectItem key={r} value={r}>{roleText(r)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            </div>
            {agreement && (
              <p aria-label="Agreement" className="flex items-center gap-2 text-sm text-text">
                <Badge tone={minor ? "warning" : "accent"} size="sm">{minor ? "Minor" : "Adult"}</Badge>
                {agreementText(agreement)}
              </p>
            )}
            {minor && (asksGuardian || asksRelation) && (
              <div className="grid grid-cols-2 gap-3">
                {asksGuardian && (
                  <Field label="Parent or legal guardian" htmlFor="person-guardian" required
                    error={problems[GUARDIAN_NAME_KEY]}>
                    <Input id="person-guardian" autoComplete="off" value={guardianName}
                      onChange={(e) => setGuardianName(e.target.value)} />
                  </Field>
                )}
                {asksRelation && (
                  <Field label="Relationship to the client" htmlFor="person-relation" required
                    error={problems[GUARDIAN_RELATION_KEY]}>
                    <Input id="person-relation" autoComplete="off" placeholder="Mother" value={guardianRelation}
                      onChange={(e) => setGuardianRelation(e.target.value)} />
                  </Field>
                )}
              </div>
            )}
            <Field label="Phone" htmlFor="person-phone" required error={problems.phone}
              hint={thisPerson
                ? "This person's own number."
                : "The signing link is texted here. Keep the caller's number for a child, or type the person's own."}>
              {/* The +1 is fixed; the rep types the ten digits after it (ACL-653). */}
              <UsPhoneInput id="person-phone" value={phone} disabled={locked || thisPerson}
                invalid={Boolean(problems.phone)} onChange={setPhone} />
            </Field>
            {!thisPerson && (
              <Field label="E-mail" htmlFor="person-email" error={problems.email}
                hint="The signing link is e-mailed here too; the agreement asks for it.">
                <Input id="person-email" type="email" autoComplete="off" value={email} disabled={locked}
                  invalid={Boolean(problems.email)} onChange={(e) => setEmail(e.target.value)} />
              </Field>
            )}
            {dialWith && (
              <div className="rounded-md border border-border px-3 py-2.5">
                <label htmlFor="person-call-them" className="flex cursor-pointer items-center gap-2.5 text-sm text-text">
                  <input
                    id="person-call-them"
                    type="checkbox"
                    className="size-3.5 shrink-0 accent-accent-9"
                    checked={ticked}
                    disabled={locked}
                    onChange={(e) => setCallThem(e.target.checked)}
                  />
                  <span>Add them to the dial list</span>
                </label>
                <p className="mt-1 pl-6 text-xs text-text-muted">
                  {ticked
                    ? dialWith.callId
                      ? "On the dial list now; the dialer calls them as a new lead once this call is over."
                      : "On the dial list now; the dialer calls them as a new lead."
                    : ownNumber
                      ? `Not called by itself: this number is already called through ${sourceName}'s record.`
                      : "Nobody dials them: you call them yourself, or book a callback. A supervisor can list them later."}
                </p>
              </div>
            )}

            <section aria-label="Copied from this record" className="rounded-md border border-border px-3 py-2.5">
              <p className="text-2xs font-semibold uppercase tracking-wide text-text-subtle">
                {sameAccident && shared.size > 0 && sourceNumber ? `Shared in accident ${sourceNumber}` : `From ${sourceName}'s record`}
              </p>
              {!sameAccident ? (
                <p className="mt-1 text-xs text-text-muted">
                  A new accident starts with no details of the crash; {sourceName}'s stay on their record.
                </p>
              ) : fields.isLoading ? (
                <p className="mt-1 text-xs text-text-muted">Reading the accident's details…</p>
              ) : facts.length > 0 ? (
                <dl className="mt-1 grid grid-cols-[minmax(0,10rem)_minmax(0,1fr)] gap-x-3 gap-y-0.5 text-xs">
                  {facts.map((fact) => (
                    <React.Fragment key={fact.key}>
                      <dt className="truncate text-text-muted" title={fact.title}>{fact.title}</dt>
                      <dd className="min-w-0 break-words text-text">{fact.text}</dd>
                    </React.Fragment>
                  ))}
                </dl>
              ) : (
                <p className="mt-1 text-xs text-text-muted">This record has none of the accident's details yet.</p>
              )}
              <p className="mt-1.5 text-xs text-text-muted">
                Also the firm ({source.firm?.label ?? "none yet"}), the state, the timezone and the language.
              </p>
              {sameAccident && missing.length > 0 && (
                <p className="mt-1.5 text-xs text-warning-11">
                  Not on this record: {missing.join(", ")}. Fill it in on the new record before sending the agreement.
                </p>
              )}
            </section>

            {existing && (
              <Callout tone="warning">
                <p>
                  That number already has a contact.{" "}
                  <Link to={`/contacts/${existing}`} className="font-medium underline" onClick={() => onOpenChange(false)}>
                    Open it
                  </Link>{" "}
                  and use Link someone if it is this person, or add a new record anyway.
                </p>
              </Callout>
            )}
            {failed && (
              <Callout tone="danger">
                <p>
                  {failed.step === "create" ? STEP_WORDS.create : `${contactName(made ?? { first_name: firstName, last_name: lastName }) ?? name}'s record ${STEP_WORDS[failed.step]}`}:{" "}
                  {failed.message}
                </p>
                {made && (
                  <p className="mt-1">
                    Press Try again to carry on, or{" "}
                    <Link to={`/contacts/${made.id}`} className="font-medium underline" onClick={() => onOpenChange(false)}>
                      open the record
                    </Link>
                    .
                  </p>
                )}
              </Callout>
            )}
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            {existing ? (
              <Button type="button" loading={busy} onClick={() => void run(true)}>
                Add anyway
              </Button>
            ) : (
              <Button type="submit" loading={busy}>
                {made ? "Try again" : sameAccident ? "Add to this accident" : "Start the new case"}
              </Button>
            )}
          </DialogFooter>
        </DialogForm>
      </DialogContent>
    </Dialog>
  );
}
